import React, { useEffect, useState } from "react";
import { Grid, MenuItem, TextField, Button, CircularProgress, Box } from '@mui/material';
import { getSingleOrgSeasonApi } from '../../api/orgSeasonApi';
import { useAuth0 } from "@auth0/auth0-react";

import { useParams, useNavigate } from 'react-router-dom';
import { CreateMemberCommand, MemberSummary } from "../../api/models/member";
import { OrgSeason } from "../../api/models/orgSeason";
import { createMemberApi, updateMemberAssignTeam } from '../../api/memberApi';
import { Team } from "../../api/models/team";
import { ErrorHeader } from "../../shared/errorHeader";
import { InfoBar } from "../../shared/infoBar";
import { flattenOrgSeasonToMembers } from "../../models/helpers";
import { SelectMemberSummaryPanel } from "./selectMemberSummaryPanel";
import { useApiCallWithAuth } from "../../hooks/useApiCallWithAuth";
import { useSearchParams } from 'react-router-dom';
import { BasicDropDown } from "../../shared/basicDropDown";
import { gradeClassList } from "../../models/dropDownItemViewModel";


export function AddMemberPage() {

    const [searchParams] = useSearchParams();
    const source = searchParams.get('source');

    const [orgSeason, setOrgSeason] = useState<OrgSeason | null>(null);
    const [team, setTeam] = useState<Team | null>(null);
    const [members, setMembers] = useState<MemberSummary[]>([]);
    const [matchingMembers, setMatchingMembers] = useState<MemberSummary[]>([]);

    const [memberType, setMemberType] = React.useState('Athlete');
    const [firstName, setFirstName] = React.useState('');
    const [lastName, setLastName] = React.useState('');
    const [jerseyNumber, setJerseyNumber] = React.useState('');
    const [position, setPosition] = React.useState('');
    const [gradeClass, setGradeClass] = React.useState('');

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [accessLevel, setAccessLevel] = useState<string | undefined>(undefined);
    const [isAdmin, setIsAdmin] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const params = useParams();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth0();

    const getOrgSeasonApi = useApiCallWithAuth(getSingleOrgSeasonApi);
    const addMemberApi = useApiCallWithAuth(createMemberApi);
    const addMemberToRosterApi = useApiCallWithAuth(updateMemberAssignTeam);

    const navigateUrl =
        source === "photo-capture"
            ? `/orgseason/${params.orgSeasonId}/photo-capture?team-guid=${params.teamId}`
            : `/orgseason/${params.orgSeasonId}/team/${params.teamId}`;


    useEffect(() => {
        initPage();
    }, []);

    useEffect(() => {
        if(orgSeason){
            setMembers(flattenOrgSeasonToMembers(orgSeason));
        }
        
    }, [orgSeason]);


    async function initPage() {
        if (!params.orgSeasonId) {
            throw Error('OrgSeason Id is missing');
        }

        if (!params.teamId) {
            throw Error('Team Id is missing');
        }

        setIsLoading(true);

        const result = await getOrgSeasonApi.request(params.orgSeasonId);
        const localOrgSeason = result as OrgSeason;

        if (localOrgSeason) {
            setOrgSeason(localOrgSeason);
            const team = localOrgSeason.teams.find(t => t.id === params.teamId);
            setTeam(team!);
        }
        setIsLoading(false);
    }

    //event handlers
    const onChangeMemberType = (e: React.ChangeEvent<HTMLInputElement>) => {
        setMemberType(e.target.value);
        setPosition(e.target.value);
    }

    const onChangeFirstName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFirstName(e.target.value);
    }

    const onChangeLastName = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setLastName(e.target.value);
    }

    const onChangeJerseyNumber = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setJerseyNumber(e.target.value);
    }

    const onChangePosition = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setPosition(e.target.value);
    }

    const onChangeGradeClass = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setGradeClass(e.target.value);
    }

    async function addMember(){

        const filteredMembers = members.filter(
            member =>
                member.firstName.toLowerCase() === firstName.toLowerCase() &&
                member.lastName.toLowerCase() === lastName.toLowerCase()
        );

        setMatchingMembers(filteredMembers);

        if (filteredMembers.length > 0) {
            setErrorMessage('Member already exists');
            return;
        }

        const addMemberItem: CreateMemberCommand = {
            orgSeasonId: orgSeason!.id,
            firstName: firstName,
            lastName: lastName,
            role: memberType === "Athlete" ? 'athlete' : 'leader', 
            title: memberType === "Athlete" ? undefined : memberType, //if leader, else undefined
            jerseyNumber: jerseyNumber,
            position: position,
            teamId: params.teamId!,
            gradeClass: gradeClass
        };

        //todo: continue if there is no match
        setIsLoading(true);
        try {
            console.log('Creating member');
            await createMember(addMemberItem);
            console.log('Member created');
            clearPage();
            navigate(navigateUrl);
        }
        catch (err) {

             setErrorMessage(JSON.stringify(err));
        }
        finally {
            setIsLoading(false);
        }
    }

    function clearPage() {
        setFirstName('');
        setLastName('');
        setJerseyNumber('');
        setPosition('');
        setGradeClass('');
        setMemberType('Athlete');

    }

    async function createMember(addMemberItem: CreateMemberCommand){
        await addMemberApi.request(addMemberItem);
    }

    async function addMemberToTeam(memberGuid:string, orgSeasonGuid:string, teamGuid:string){

        setIsLoading(true);

        const assignTeamCommand = {
            orgSeasonId: orgSeasonGuid,
            teamId: teamGuid,
            role: memberType === "Athlete" ? 'athlete' : 'leader', 
            position: undefined,
            title: memberType === "Athlete" ? undefined : memberType,
            jerseyNumber: jerseyNumber,
        }

        await addMemberToRosterApi.request(memberGuid,assignTeamCommand);

        setIsLoading(false);
    }

    async function handleMemberSelected(memberGuid: string) {

        //todo; need loader and error handling
        //todo: need to pass position, jersey number, and title
        await addMemberToTeam(memberGuid, params.orgSeasonId!, params.teamId!);
    }

    //todo: need some logic to deal with if this is a NEW member with the same name


    const mainContent = () => {

        return (
            <Box>
                <h1>{team?.name} Add Member</h1>

                <Grid item container spacing={2} direction='column'>
                    <Grid item>
                        <TextField select
                            label="Select"
                            value={memberType}
                            size="small"
                            onChange={onChangeMemberType}
                            helperText="Select a member type">
                            <MenuItem key='Athlete' value='Athlete'>Athlete</MenuItem>
                            <MenuItem key='Head Coach' value='Head Coach'>Head Coach</MenuItem>
                            <MenuItem key='Asst Coach' value='Asst Coach'>Asst Coach</MenuItem>
                            <MenuItem key='Team Parent' value='Team Parent'>Team Parent</MenuItem>
                            <MenuItem key='Athletic Trainer' value='Athletic Trainer'>Athletic Trainer</MenuItem>
                            <MenuItem key='Equipment Manager' value='Equipment Mgr'>Equipment Mgr</MenuItem>
                            <MenuItem key='Board' value='Board'>Board</MenuItem>
                            <MenuItem key='Other' value='Other'>Other</MenuItem>
                        </TextField>
                    </Grid>

                    <Grid item>
                        <TextField id="txtFirstName"
                            label="FirstName"
                            variant="outlined"
                            size="small"
                            value={firstName}
                            onChange={e => onChangeFirstName(e)}
                            inputProps={{ maxLength: 20 }}
                            style={{ width: 275 }}
                        />
                    </Grid>

                    <Grid item>
                        <TextField id="txtLastName"
                            label="Last Name"
                            variant="outlined"
                            size="small"
                            value={lastName}
                            inputProps={{ maxLength: 20 }}
                            style={{ width: 275 }}
                            onChange={e => onChangeLastName(e)} />
                    </Grid>

                    {(memberType === "Athlete") &&
                        <Grid item>
                            <TextField id="txtJerseyNumber"
                                label="Jersey Number"
                                variant="outlined"
                                size="small"
                                inputProps={{ maxLength: 3 }}
                                style={{ width: 200 }}
                                value={jerseyNumber}
                                onChange={e => onChangeJerseyNumber(e)} />
                        </Grid>
                    }

                    <Grid item>
                        <TextField id="txtPosition"
                            label="Position"
                            variant="outlined"
                            size="small"
                            value={position}
                            inputProps={{ maxLength: 25 }}
                            style={{ width: 275 }}
                            onChange={e => onChangePosition(e)} />
                    </Grid>

                    <Grid item>
                          <BasicDropDown id='gradeClass'
                                            label='select grade'
                                            width={275}
                                            value={gradeClass}
                                            items={gradeClassList}
                                            onChange={e=>onChangeGradeClass(e)} />
                                        
                    </Grid>

                    <Grid item>
                        {isAuthenticated && !isLoading &&
                            <Button onClick={e => addMember()} variant="contained" color="primary">
                                Add
                            </Button>}
                        
                    </Grid>

                    <Grid item>

                        {matchingMembers && matchingMembers.length > 0 &&  <SelectMemberSummaryPanel member={matchingMembers[0]} onSelected={e => handleMemberSelected(matchingMembers[0].id)} />}


                    </Grid>


                </Grid>

            </Box>
        );
    }

    return (<Box>
                 <InfoBar  navigateUrl={navigateUrl}  />
                <ErrorHeader errorMessage={errorMessage} />
        {isLoading && <CircularProgress />}
        {team && mainContent()}
    </Box>
    );

}